import AppShell, { type AppShellProps } from '@/components/AppShell'
import RegistrationForm from '@/components/RegistrationForm'
import RegistrationPageLayout from '@/components/RegistrationPageLayout'
import RegistrationStepIndicator from '@/components/RegistrationStepIndicator'
import type { CustomFieldDef, EventTheme, InitialFormValues, RegistrationField, TitleOption } from '@/lib/registrationFormShared'

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  registrationFields: RegistrationField[]
  customFields: CustomFieldDef[]
  userTypes: string[]
  bloodTypes: string[]
  genderOptions: string[]
  countries: string[]
  titleFieldType?: 'text' | 'select'
  titleOptions?: TitleOption[]
  positionFieldType?: 'text' | 'select'
  positionOptions?: string[]
  lockedUserType?: string | null
  registrationLinkSlug?: string | null
  errors: Record<string, string>
  currentLocale: string
  locales: string[]
  mode: 'create' | 'edit'
  multiStep: boolean
  wizard: {
    currentStep: number
    totalSteps: number
    hasAgendaStep: boolean
  } | null
  initialForm: InitialFormValues | null
  preselectedSession?: {
    id: number
    description: string
    time: string
    dayNumber?: number
  } | null
  sessionId?: number | null
  urls: AppShellProps['urls'] & {
    submit: string
    landing: string
    form: string
  }
  translations: AppShellProps['translations'] & {
    title: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    badgeType: string
    gender: string
    country: string
    city: string
    age: string
    idNumber: string
    passportNumber: string
    bloodType: string
    image: string
    pleaseSelect: string
    otherTitle?: string
    register: string
    saveChanges: string
    registrationClosed: string
    requiredFields: string
    privacyPolicy: string
    privacyPolicyAgreement: string
    back: string
    stepLanding: string
    stepForm: string
    stepAgenda: string
    stepConfirmation: string
    registeringForSession: string
  }
}

export default function RegistrationFormStep(props: Props) {
  const {
    event, registrationFields, customFields, userTypes, bloodTypes,
    genderOptions, countries, titleFieldType, titleOptions, positionFieldType, positionOptions, lockedUserType, registrationLinkSlug, errors, currentLocale, locales, mode, wizard,
    initialForm, preselectedSession, sessionId, urls, translations: t, ...shell
  } = props

  const stepIndicator = wizard ? (
    <RegistrationStepIndicator
      wizard={wizard}
      labels={{ stepLanding: t.stepLanding, stepForm: t.stepForm, stepAgenda: t.stepAgenda, stepConfirmation: t.stepConfirmation }}
    />
  ) : null

  const sessionBanner = preselectedSession ? (
    <div className="ev-reg-session-banner mb-4 rounded-md border px-4 py-3 text-sm">
      <p className="ev-reg-session-banner-label text-xs uppercase tracking-wide opacity-70">{t.registeringForSession}</p>
      {preselectedSession.time && <p className="ev-reg-session-banner-time mt-1 font-mono text-xs opacity-70">{preselectedSession.time}</p>}
      <p className="ev-reg-session-banner-title mt-1 font-medium">{preselectedSession.description}</p>
    </div>
  ) : null

  return (
    <RegistrationPageLayout event={event} currentLocale={currentLocale} locales={locales} urls={urls} translations={t} pageVariant="form" registrationLinkSlug={registrationLinkSlug} {...shell}>
      <RegistrationForm
        event={event}
        registrationFields={registrationFields}
        customFields={customFields}
        userTypes={userTypes}
        bloodTypes={bloodTypes}
        genderOptions={genderOptions}
        countries={countries}
        titleFieldType={titleFieldType}
        titleOptions={titleOptions}
        positionFieldType={positionFieldType}
        positionOptions={positionOptions}
        lockedUserType={lockedUserType}
        registrationLinkSlug={registrationLinkSlug}
        errors={errors}
        currentLocale={currentLocale}
        translations={t}
        submitUrl={urls.submit}
        submitMethod={mode === 'edit' ? 'patch' : 'post'}
        mode={mode}
        initialForm={initialForm}
        backUrl={urls.landing}
        headerExtra={(
          <>
            {stepIndicator}
            {sessionBanner}
          </>
        )}
        sessionId={sessionId}
      />
    </RegistrationPageLayout>
  )
}
